import React from 'react';
import { ChangeEvent } from 'react';
import { Box, FormControlLabel, FormGroup, Stack, Typography, useTheme } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconPhoneCall } from '@tabler/icons-react';

import { PhoneNumbersField } from '@shared/components/PhoneNumbersField';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { ExpandableSettingsBlock, StaticSettingsBlock } from './SettingsBlocks';
import { ToolPanelProps } from './types';

export const CallRoutingSettingsPanel: React.FC<ToolPanelProps> = ({
  isReadOnly,
  expanded,
  onExpandedChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control, setValue } = useFormContext<AgentToolsFormValue>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ExpandableSettingsBlock
        id="transfer"
        expanded={expanded.transfer}
        onChange={(nextExpanded) => onExpandedChange('transfer', nextExpanded)}
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                'svg path': {
                  stroke: theme.palette.getContrastText(theme.palette.background.default),
                },
              }}>
              <IconPhoneCall width={16} />
              <Typography fontSize="16px" fontWeight={600}>
                {t('campaignPage.transferCallCheckbox')}
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                control={
                  <Controller
                    name="transferTool.enabled"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={isReadOnly}
                        inputProps={{
                          'aria-label': t('campaignPage.transferCallCheckbox'),
                        }}
                        onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.checked);
                          onExpandedChange('transfer', e.target.checked);
                          if (!e.target.checked) {
                            setValue('transferTool.phoneNumbers', []);
                          }
                        }}
                      />
                    )}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }>
        <Box sx={{ mt: 2 }}>
          <Controller
            name="transferTool.phoneNumbers"
            control={control}
            render={({ field }) => (
              <PhoneNumbersField
                isTransfer
                phoneNumbers={field.value || []}
                onChange={(phoneNumbers) => {
                  field.onChange(phoneNumbers);
                  if (phoneNumbers.length > 0) {
                    setValue('transferTool.enabled', true);
                  } else {
                    setValue('transferTool.enabled', false);
                  }
                }}
                disabled={isReadOnly}
              />
            )}
          />
        </Box>
      </ExpandableSettingsBlock>

      <StaticSettingsBlock
        header={
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                'svg path': {
                  stroke: theme.palette.getContrastText(theme.palette.background.default),
                },
              }}>
              <IconPhoneCall width={16} />
              <Typography fontSize="16px" fontWeight={600}>
                {t('campaignPage.callHandoffSignalCheckbox')}
              </Typography>
            </Stack>
            <FormGroup>
              <FormControlLabel
                sx={{ marginRight: 0 }}
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                control={
                  <Controller
                    name="callHandoffSignalTool.enabled"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={isReadOnly}
                        inputProps={{
                          'aria-label': t('campaignPage.callHandoffSignalCheckbox'),
                        }}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                }
                label=""
              />
            </FormGroup>
          </Stack>
        }
      />
    </Box>
  );
};
